import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { useEffect, useState } from 'react';
import { loginRequest } from './authConfig';
import AdvertisementForm from './components/AdvertisementForm';
import AdvertisementList from './components/AdvertisementList';
import { AuthButton } from './components/AuthButton';
import { IndexStatistics } from './components/IndexStatistics';
import PromotedResultForm from './components/PromotedResultForm';
import { RecordBoosting } from './components/RecordBoosting';
import { SearchModeIndexSelector } from './components/SearchModeIndexSelector';
import SponsoredResults from './components/SponsoredResults';
import { UATSessions } from './components/UATSessions';
import { UserFeedbackList } from './components/UserFeedbackList';
import UserGuide from './components/UserGuide';
import { VectorSearchConfig } from './components/VectorSearchConfig';
import { WeightManager } from './components/WeightManager';
import { FuzzinessSettings } from './components/FuzzinessSettings';
import { WeightMultiEdit } from './components/WeightMultiEdit';
import type { Advertisement, AllIndexWeights, IndexWeightConfig } from './types';
import { authenticatedFetch } from './utils/api';

const AVAILABLE_INDICES = [
  // ASM Info and Products (reordered to match search bar)
  { name: 'asmi-wp-pages', displayName: 'ASM Information' },
  { name: 'asmi-wp-products,asmi-marketing-gear-store', displayName: 'Products' }, // Combined products index
  { name: 'asmi-wp-events', displayName: 'ASM Education' },
  { name: 'asmi-pages-events', displayName: 'Event Pages' },
  { name: 'asmi-wp-asm-news', displayName: 'Industry News' },
  { name: 'asmi-wp-videos', displayName: 'Videos' },
  // Digital Book Content (consolidated group)
  { name: 'asmi-dl-books', displayName: 'Digital Books' },
  { name: 'asmi-dl-chapters', displayName: 'Digital Book Chapters' },
  { name: 'asmi-dl-chapter-images', displayName: 'Digital Book Images' },
  { name: 'asmi-dl-chapter-tables', displayName: 'Digital Book Tables' },
  // Other Digital Library Content
  { name: 'asmi-dl-technical_articles', displayName: 'Magazine Articles' },
  { name: 'asmi-wp-news_articles', displayName: 'ASM Member News' },
  { name: 'asmi-dl-proceedings', displayName: 'Conference Papers' },
  { name: 'asmi-dl-datasheets', displayName: 'Alloy Digest Datasheets' },
  // Materials Data indexes
  { name: 'asmi-mat-matdb-materials', displayName: 'Materials Database' },
  { name: 'asmi-mat-alloyfinder-alloy-finder', displayName: 'Alloy Finder' },
  { name: 'asmi-mat-datasheets-data-sheets-and-diagrams', displayName: 'Materials Datasheets' },
  { name: 'asmi-mat-grantami_mgd-micrograph-data', displayName: 'Micrograph Data' },
  { name: 'asmi-mat-corrosion-uniform-corrosion-data', displayName: 'Corrosion Data' },
  { name: 'asmi-mat-corrosion-environments', displayName: 'Corrosion Environments' },
  { name: 'asmi-mat-matdb-documents', displayName: 'Materials Documents' },
  { name: 'asmi-mat-datasheets-data-sheet-sources', displayName: 'Materials Data Sources' },
  { name: 'asmi-mat-matdb-reference', displayName: 'Materials References' },
  { name: 'asmi-mat-corrosion-design-data', displayName: 'Corrosion Design Data' },
  { name: 'asmi-mat-corrosion-references', displayName: 'Corrosion References' },
  { name: 'asmi-mat-alloyfinder-organizations', displayName: 'Alloy Organizations' },
  { name: 'asmi-mat-corrosion-materials', displayName: 'Corrosion Materials' },
  { name: 'asmi-mat-corrosion-high-temperature-oxidation-data', displayName: 'High-Temperature Oxidation Data' },
  { name: 'asmi-mat-alloy-phase-diagrams', displayName: 'Phase Diagrams' },
  { name: 'asmi-mat-alloy-phase-structures', displayName: 'Crystallographic Structures' },
  { name: 'asmi-mat-medical-materials', displayName: 'Medical Materials Database' },
];

interface SponsoredResult {
  id: string;
  keywords: string[];
  title: string;
  description: string;
  url?: string;
  forceResult?: {
    index: string;
    resultId: string;
    resultTitle: string;
  };
  category: 'sponsored' | 'targeted' | 'priority';
  startDate: string;
  endDate: string;
  priority: number;
  active: boolean;
  createdAt: string;
}

// Valid tab names for URL routing
type TabName = 'weights' | 'sponsored' | 'boosting' | 'vector' | 'advertisements' | 'modes' | 'preview' | 'guide' | 'statistics' | 'uat' | 'feedback' | 'fuzziness';

const VALID_TABS: TabName[] = ['weights', 'sponsored', 'boosting', 'vector', 'advertisements', 'modes', 'preview', 'guide', 'statistics', 'uat', 'feedback', 'fuzziness'];

// Get initial tab from URL hash
function getInitialTab(): TabName {
  const hash = window.location.hash.slice(1); // Remove the #
  if (VALID_TABS.includes(hash as TabName)) {
    return hash as TabName;
  }
  return 'weights';
}

function App() {
  const { instance, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [allWeights, setAllWeights] = useState<AllIndexWeights | null>(null);
  const [activeIndex, setActiveIndex] = useState('asmi-dl-books');
  const [activeTab, setActiveTab] = useState<TabName>(getInitialTab);
  const [weightViewMode, setWeightViewMode] = useState<'individual' | 'multi-edit'>('individual');
  const [currentPage, setCurrentPage] = useState<'list' | 'form'>('list');
  const [editingResult, setEditingResult] = useState<SponsoredResult | null>(null);
  const [editingAd, setEditingAd] = useState<Advertisement | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [weightsSearchMode, setWeightsSearchMode] = useState<'regular' | 'research'>('regular');
  const [searchModesConfig, setSearchModesConfig] = useState<{
    regular: { enabledIndexes: string[] };
    research: { enabledIndexes: string[] };
  } | null>(null);

  // Update URL hash when tab changes
  useEffect(() => {
    window.location.hash = activeTab;
  }, [activeTab]);

  // Listen for browser back/forward navigation
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.slice(1);
      if (VALID_TABS.includes(hash as TabName)) {
        setActiveTab(hash as TabName);
      }
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  const handleLogin = async () => {
    try {
      setError(null); // Clear any previous errors
      await instance.loginPopup(loginRequest);
    } catch (error) {
      console.error('Login failed:', error);
      setError('Authentication failed. Please try again.');
    }
  };

  // Clear error when successfully authenticated
  useEffect(() => {
    if (isAuthenticated) {
      setError(null);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    fetchWeights();
    fetchSearchModes();
  }, []);

  // Reset to list view when switching tabs
  useEffect(() => {
    if (activeTab === 'sponsored' || activeTab === 'advertisements') {
      setCurrentPage('list');
      setEditingResult(null);
      setEditingAd(null);
    }
  }, [activeTab]);

  const fetchSearchModes = async () => {
    try {
      const apiUrl = import.meta.env.VITE_API_URL || '';
      const response = await fetch(`${apiUrl}/api/search-modes`);
      if (response.ok) {
        const data = await response.json();
        setSearchModesConfig(data);
      }
    } catch (error) {
      console.error('Failed to fetch search modes:', error);
    }
  };

  const fetchWeights = async () => {
    try {
      setLoading(true);
      const apiUrl = import.meta.env.VITE_API_URL || '';
      const response = await fetch(`${apiUrl}/api/weights/all`);
      if (!response.ok) {
        throw new Error('Failed to fetch weights');
      }
      const data = await response.json();
      setAllWeights(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const getCurrentConfig = (): IndexWeightConfig | null => {
    if (!allWeights) return null;
    return allWeights.indices.find((idx) => idx.indexName === activeIndex) || null;
  };

  // Filter AVAILABLE_INDICES based on the selected search mode
  const getFilteredIndices = () => {
    if (!searchModesConfig) return AVAILABLE_INDICES;
    const enabledIndexes = searchModesConfig[weightsSearchMode].enabledIndexes;
    return AVAILABLE_INDICES.filter((idx) => enabledIndexes.includes(idx.name));
  };

  const handleSave = async (config: IndexWeightConfig) => {
    try {
      setSaving(true);
      setSaveSuccess(false);
      const apiUrl = import.meta.env.VITE_API_URL || '';
      const response = await authenticatedFetch(`${apiUrl}/api/weights/${encodeURIComponent(config.indexName)}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(config),
      });

      if (!response.ok) {
        throw new Error('Failed to save configuration');
      }

      await fetchWeights();
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveMultiple = async (configs: IndexWeightConfig[]) => {
    try {
      setSaving(true);
      setSaveSuccess(false);
      const apiUrl = import.meta.env.VITE_API_URL || '';

      // Save all configs in parallel
      const savePromises = configs.map((config) =>
        authenticatedFetch(`${apiUrl}/api/weights/${encodeURIComponent(config.indexName)}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(config),
        })
      );

      const responses = await Promise.all(savePromises);

      // Check if any failed
      const failedSaves = responses.filter((r) => !r.ok);
      if (failedSaves.length > 0) {
        throw new Error(`Failed to save ${failedSaves.length} configuration(s)`);
      }

      // Only fetch weights once after all saves complete
      await fetchWeights();
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save configurations');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    const currentConfig = getCurrentConfig();
    if (!currentConfig) return;

    try {
      setSaving(true);
      const apiUrl = import.meta.env.VITE_API_URL || '';
      const response = await authenticatedFetch(`${apiUrl}/api/weights/reset/${currentConfig.indexName}`, {
        method: 'POST',
      });

      if (!response.ok) {
        throw new Error('Failed to reset configuration');
      }

      await fetchWeights();
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reset configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleAddPromotedResult = () => {
    setEditingResult(null);
    setCurrentPage('form');
  };

  const handleEditPromotedResult = (result: SponsoredResult) => {
    setEditingResult(result);
    setCurrentPage('form');
  };

  const handleFormSave = () => {
    setCurrentPage('list');
    setEditingResult(null);
  };

  const handleFormCancel = () => {
    setCurrentPage('list');
    setEditingResult(null);
  };

  const handleAddAd = () => {
    setEditingAd(null);
    setCurrentPage('form');
  };

  const handleEditAd = (ad: Advertisement) => {
    setEditingAd(ad);
    setCurrentPage('form');
  };

  const handleAdFormSave = () => {
    setCurrentPage('list');
    setEditingAd(null);
  };

  const handleAdFormCancel = () => {
    setCurrentPage('list');
    setEditingAd(null);
  };

  const handleRecordBoostingSave = async (config: any) => {
    try {
      setSaving(true);
      setSaveSuccess(false);
      const apiUrl = import.meta.env.VITE_API_URL || '';
      const response = await authenticatedFetch(`${apiUrl}/api/record-boosting`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(config),
      });

      if (!response.ok) {
        throw new Error('Failed to save record boosting configuration');
      }

      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save record boosting configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleRecordBoostingReset = async () => {
    try {
      setSaving(true);
      const apiUrl = import.meta.env.VITE_API_URL || '';

      // Fetch the default config
      const defaultConfig = {
        defaultBoost: 1.0,
        indices: AVAILABLE_INDICES.map((idx) => ({
          indexName: idx.name,
          displayName: idx.displayName,
          boost: idx.name === 'asmi-dl-datasheets' ? 2.0 : 1.0,
          enabled: true,
        })),
        lastUpdated: new Date().toISOString(),
      };

      const response = await authenticatedFetch(`${apiUrl}/api/record-boosting`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(defaultConfig),
      });

      if (!response.ok) {
        throw new Error('Failed to reset record boosting configuration');
      }

      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);

      // Force a page reload to fetch the reset config
      window.location.reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reset record boosting configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleVectorSearchSave = async (config: any) => {
    try {
      setSaving(true);
      setSaveSuccess(false);
      const apiUrl = import.meta.env.VITE_API_URL || '';
      const response = await authenticatedFetch(`${apiUrl}/api/vector-search-config`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(config),
      });

      if (!response.ok) {
        throw new Error('Failed to save vector search configuration');
      }

      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save vector search configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleVectorSearchReset = async () => {
    try {
      setSaving(true);
      const apiUrl = import.meta.env.VITE_API_URL || '';

      // Fetch the default config (based on DEFAULT_CONFIG from VectorSearchConfig component)
      const defaultConfig = {
        enabled: false,
        apiEndpoint: '',
        apiKey: '',
        indexPattern: 'asmi-*',
        embeddingModel: 'text-embedding-3-small',
        searchBoost: 1.0,
        maxResults: 50,
        minScore: 0.7,
      };

      const response = await authenticatedFetch(`${apiUrl}/api/vector-search-config`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(defaultConfig),
      });

      if (!response.ok) {
        throw new Error('Failed to reset vector search configuration');
      }

      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);

      // Force a page reload to fetch the reset config
      window.location.reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reset vector search configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleSearchModesSave = async (config: any) => {
    try {
      setSaving(true);
      setSaveSuccess(false);
      const apiUrl = import.meta.env.VITE_API_URL || '';
      const response = await authenticatedFetch(`${apiUrl}/api/search-modes`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(config),
      });

      if (!response.ok) {
        throw new Error('Failed to save search modes configuration');
      }

      // Refetch search modes to update filters in other components
      await fetchSearchModes();

      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save search modes configuration');
    } finally {
      setSaving(false);
    }
  };

  // Show login screen when not authenticated
  if (inProgress === 'none' && !isAuthenticated) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-zinc-50">
        <div className="flex flex-col items-center max-w-md p-8 bg-white rounded-lg shadow-lg">
          <img src="/ASM_Logo_White_Horizontal.png" alt="ASM International" className="h-12 brightness-0 mb-6" />
          <h1 className="text-2xl font-bold text-zinc-900 mb-2">Universal Admin Portal</h1>
          <p className="text-zinc-600 text-center mb-6">
            Please sign in with your Azure AD account to access the admin portal.
          </p>
          <button
            type="button"
            onClick={handleLogin}
            className="w-full rounded-lg bg-blue-600 px-6 py-3 text-base font-medium text-white hover:bg-blue-700 transition-colors"
          >
            Sign In with Microsoft
          </button>
          {error && <div className="mt-4 text-sm text-red-600 text-center">{error}</div>}
        </div>
      </div>
    );
  }

  // Show loading while authentication is in progress or loading data
  if (inProgress !== 'none' || loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-zinc-50">
        <div className="flex flex-col items-center">
          <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
          <p className="mt-4 text-lg text-zinc-600">
            {inProgress !== 'none' ? 'Authenticating...' : 'Loading configuration...'}
          </p>
        </div>
      </div>
    );
  }

  if (error && !allWeights) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <div className="text-lg text-red-600 mb-4">Error: {error}</div>
          <button
            type="button"
            onClick={fetchWeights}
            className="rounded-md bg-zinc-900 px-4 py-2 text-sm font-medium text-white hover:bg-zinc-800"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const currentConfig = getCurrentConfig();

  return (
    <div className="flex h-screen overflow-hidden bg-zinc-50">
      {/* Mobile menu button */}
      <button
        type="button"
        onClick={() => setSidebarOpen(!sidebarOpen)}
        className="lg:hidden fixed top-3 left-3 z-50 rounded-lg bg-zinc-900 p-2.5 text-white hover:bg-zinc-800 shadow-lg"
        aria-label="Toggle menu"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          {sidebarOpen ? (
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          ) : (
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          )}
        </svg>
      </button>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          role="button"
          tabIndex={0}
          aria-label="Close menu"
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-30"
          onClick={() => setSidebarOpen(false)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              setSidebarOpen(false);
            }
          }}
        />
      )}

      {/* Sidebar */}
      <div
        className={`flex w-64 flex-col border-r border-zinc-200 bg-white fixed lg:static inset-y-0 left-0 z-40 transform transition-transform duration-300 ease-in-out ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
      >
        {/* Logo */}
        <div className="flex h-16 items-center border-b border-zinc-200 px-6">
          <h1 className="text-lg font-semibold text-zinc-900">Universal Search Admin</h1>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto p-4">
          <div className="space-y-1">
            <button
              type="button"
              onClick={() => {
                setActiveTab('modes');
                setSidebarOpen(false);
              }}
              className={`flex w-full items-center rounded-lg px-3 py-2 text-sm font-medium transition-colors cursor-pointer ${
                activeTab === 'modes'
                  ? 'bg-zinc-100 text-zinc-900'
                  : 'text-zinc-700 hover:bg-zinc-50 hover:text-zinc-900'
              }`}
            >
              Search Modes
            </button>
            <button
              type="button"
              onClick={() => {
                setActiveTab('weights');
                setSidebarOpen(false);
              }}
              className={`flex w-full items-center rounded-lg px-3 py-2 text-sm font-medium transition-colors cursor-pointer ${
                activeTab === 'weights'
                  ? 'bg-zinc-100 text-zinc-900'
                  : 'text-zinc-700 hover:bg-zinc-50 hover:text-zinc-900'
              }`}
            >
              Search Weights
            </button>
            <button
              type="button"
              onClick={() => {
                setActiveTab('boosting');
                setSidebarOpen(false);
              }}
              className={`flex w-full items-center rounded-lg px-3 py-2 text-sm font-medium transition-colors cursor-pointer ${
                activeTab === 'boosting'
                  ? 'bg-zinc-100 text-zinc-900'
                  : 'text-zinc-700 hover:bg-zinc-50 hover:text-zinc-900'
              }`}
            >
              Record Boosting
            </button>
            <button
              type="button"
              onClick={() => {
                setActiveTab('vector');
                setSidebarOpen(false);
              }}
              className={`flex w-full items-center rounded-lg px-3 py-2 text-sm font-medium transition-colors cursor-pointer ${
                activeTab === 'vector'
                  ? 'bg-zinc-100 text-zinc-900'
                  : 'text-zinc-700 hover:bg-zinc-50 hover:text-zinc-900'
              }`}
            >
              Vector Search
            </button>
            <button
              type="button"
              onClick={() => {
                setActiveTab('fuzziness');
                setSidebarOpen(false);
              }}
              className={`flex w-full items-center rounded-lg px-3 py-2 text-sm font-medium transition-colors cursor-pointer ${
                activeTab === 'fuzziness'
                  ? 'bg-zinc-100 text-zinc-900'
                  : 'text-zinc-700 hover:bg-zinc-50 hover:text-zinc-900'
              }`}
            >
              Fuzziness Settings
            </button>
            <button
              type="button"
              onClick={() => {
                setActiveTab('sponsored');
                setSidebarOpen(false);
              }}
              className={`flex w-full items-center rounded-lg px-3 py-2 text-sm font-medium transition-colors cursor-pointer ${
                activeTab === 'sponsored'
                  ? 'bg-zinc-100 text-zinc-900'
                  : 'text-zinc-700 hover:bg-zinc-50 hover:text-zinc-900'
              }`}
            >
              Results Management
            </button>
            <button
              type="button"
              onClick={() => {
                setActiveTab('advertisements');
                setSidebarOpen(false);
              }}
              className={`flex w-full items-center rounded-lg px-3 py-2 text-sm font-medium transition-colors cursor-pointer ${
                activeTab === 'advertisements'
                  ? 'bg-zinc-100 text-zinc-900'
                  : 'text-zinc-700 hover:bg-zinc-50 hover:text-zinc-900'
              }`}
            >
              Advertisements
            </button>
            <button
              type="button"
              onClick={() => {
                setActiveTab('preview');
                setSidebarOpen(false);
              }}
              className={`flex w-full items-center rounded-lg px-3 py-2 text-sm font-medium transition-colors cursor-pointer ${
                activeTab === 'preview'
                  ? 'bg-zinc-100 text-zinc-900'
                  : 'text-zinc-700 hover:bg-zinc-50 hover:text-zinc-900'
              }`}
            >
              OpenSearch Dashboard
            </button>
            <button
              type="button"
              onClick={() => {
                setActiveTab('statistics');
                setSidebarOpen(false);
              }}
              className={`flex w-full items-center rounded-lg px-3 py-2 text-sm font-medium transition-colors cursor-pointer ${
                activeTab === 'statistics'
                  ? 'bg-zinc-100 text-zinc-900'
                  : 'text-zinc-700 hover:bg-zinc-50 hover:text-zinc-900'
              }`}
            >
              Index Statistics
            </button>
            <button
              type="button"
              onClick={() => {
                setActiveTab('uat');
                setSidebarOpen(false);
              }}
              className={`flex w-full items-center rounded-lg px-3 py-2 text-sm font-medium transition-colors cursor-pointer ${
                activeTab === 'uat'
                  ? 'bg-zinc-100 text-zinc-900'
                  : 'text-zinc-700 hover:bg-zinc-50 hover:text-zinc-900'
              }`}
            >
              UAT Sessions
            </button>
            <button
              type="button"
              onClick={() => {
                setActiveTab('feedback');
                setSidebarOpen(false);
              }}
              className={`flex w-full items-center rounded-lg px-3 py-2 text-sm font-medium transition-colors cursor-pointer ${
                activeTab === 'feedback'
                  ? 'bg-zinc-100 text-zinc-900'
                  : 'text-zinc-700 hover:bg-zinc-50 hover:text-zinc-900'
              }`}
            >
              User Feedback
            </button>
          </div>
        </nav>

        {/* ASM Logo */}
        <div className="flex justify-center p-4">
          <img src="/ASM_Logo_White_Horizontal.png" alt="ASM International" className="h-7 brightness-0" />
        </div>

        {/* Footer */}
        <div className="border-t border-zinc-200 p-4">
          <button
            type="button"
            onClick={() => {
              setActiveTab('guide');
              setSidebarOpen(false);
            }}
            className={`flex w-full items-center rounded-lg px-3 py-2 text-sm font-medium transition-colors cursor-pointer ${
              activeTab === 'guide' ? 'bg-zinc-100 text-zinc-900' : 'text-zinc-700 hover:bg-zinc-50 hover:text-zinc-900'
            }`}
          >
            User Guide
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex flex-1 flex-col overflow-hidden lg:ml-0">
        {/* Header */}
        <header className="flex h-16 items-center justify-between border-b border-zinc-200 bg-white px-4 sm:px-8 lg:pt-0">
          <div className="truncate ml-12 lg:ml-0">
            <h2 className="text-base sm:text-lg font-semibold text-zinc-900 truncate">
              {activeTab === 'weights' && 'Search Weights'}
              {activeTab === 'boosting' && 'Record Boosting'}
              {activeTab === 'vector' && 'Vector Search'}
              {activeTab === 'fuzziness' && 'Fuzziness Settings'}
              {activeTab === 'sponsored' && 'Results Management'}
              {activeTab === 'advertisements' && 'Advertisements'}
              {activeTab === 'modes' && 'Search Modes'}
              {activeTab === 'preview' && 'OpenSearch Dashboard'}
              {activeTab === 'statistics' && 'Index Statistics'}
              {activeTab === 'uat' && 'UAT Sessions'}
              {activeTab === 'feedback' && 'User Feedback'}
              {activeTab === 'guide' && 'User Guide'}
            </h2>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 items-center">
            {saveSuccess && (
              <div className="rounded-md bg-green-50 px-3 py-2 text-xs sm:text-sm font-medium text-green-800">
                Saved!
              </div>
            )}
            {error && (
              <div className="rounded-md bg-red-50 px-3 py-2 text-xs sm:text-sm font-medium text-red-800 max-w-xs truncate">
                {error}
              </div>
            )}
            <AuthButton />
          </div>
        </header>

        {/* Content Area */}
        <main className="flex-1 overflow-y-auto bg-zinc-50 p-4 sm:p-6 lg:p-8 pt-4 lg:pt-8">
          {activeTab === 'weights' && (
            <div className="space-y-4 sm:space-y-6">
              {/* View Mode Toggle */}
              <div className="flex flex-col sm:flex-row gap-2 border-b border-zinc-200 pb-4">
                <button
                  type="button"
                  onClick={() => setWeightViewMode('individual')}
                  className={`rounded-lg px-4 py-2 text-xs sm:text-sm font-medium transition-colors cursor-pointer ${
                    weightViewMode === 'individual'
                      ? 'bg-zinc-900 text-white'
                      : 'bg-white text-zinc-700 hover:bg-zinc-50 border border-zinc-300'
                  }`}
                >
                  Individual View
                </button>
                <button
                  type="button"
                  onClick={() => setWeightViewMode('multi-edit')}
                  className={`rounded-lg px-4 py-2 text-xs sm:text-sm font-medium transition-colors cursor-pointer ${
                    weightViewMode === 'multi-edit'
                      ? 'bg-zinc-900 text-white'
                      : 'bg-white text-zinc-700 hover:bg-zinc-50 border border-zinc-300'
                  }`}
                >
                  Multi-Index Editor
                </button>
              </div>

              {weightViewMode === 'individual' ? (
                <div className="space-y-4 sm:space-y-6">
                  {/* Search Mode Selector for Individual View */}
                  <div className="space-y-3 pb-4 border-b border-zinc-200">
                    <h4 className="text-sm font-medium text-zinc-900">Search Mode:</h4>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        type="button"
                        onClick={() => setWeightsSearchMode('regular')}
                        className={`px-4 py-3 rounded-lg text-left transition-all ${
                          weightsSearchMode === 'regular'
                            ? 'bg-zinc-900 text-white shadow-lg'
                            : 'bg-white text-zinc-700 border-2 border-zinc-200 hover:border-zinc-300'
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-lg">🔍</span>
                          <h3 className="text-sm font-semibold">General Search</h3>
                        </div>
                        <p className="text-xs opacity-75">All content types for general users</p>
                      </button>

                      <button
                        type="button"
                        onClick={() => setWeightsSearchMode('research')}
                        className={`px-4 py-3 rounded-lg text-left transition-all ${
                          weightsSearchMode === 'research'
                            ? 'bg-blue-600 text-white shadow-lg'
                            : 'bg-white text-zinc-700 border-2 border-zinc-200 hover:border-zinc-300'
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-lg">🔬</span>
                          <h3 className="text-sm font-semibold">Research Mode</h3>
                        </div>
                        <p className="text-xs opacity-75">Academic & technical content only</p>
                      </button>
                    </div>
                    <p className="text-xs text-zinc-500 italic">
                      💡 Note: Only indexes enabled in the selected search mode are shown below. To enable/disable
                      indexes for each mode, use the "Search Modes" section.
                    </p>
                  </div>

                  {/* Index Tabs */}
                  <div className="flex flex-wrap gap-2 max-h-48 sm:max-h-none overflow-y-auto sm:overflow-visible">
                    {getFilteredIndices().map((index) => (
                      <button
                        type="button"
                        key={index.name}
                        onClick={() => setActiveIndex(index.name)}
                        className={`rounded-md px-2 sm:px-3 py-1.5 text-xs sm:text-sm font-medium transition-colors cursor-pointer ${
                          activeIndex === index.name
                            ? 'bg-zinc-900 text-white'
                            : 'bg-white text-zinc-700 hover:bg-zinc-50 border border-zinc-300'
                        }`}
                      >
                        {index.displayName}
                      </button>
                    ))}
                  </div>

                  {/* Weight Manager */}
                  {!currentConfig ? (
                    <div className="flex flex-col items-center justify-center py-16 rounded-lg border border-zinc-200 bg-white">
                      <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
                      <p className="mt-4 text-zinc-600">Loading weight configuration...</p>
                    </div>
                  ) : (
                    <div className="rounded-lg border border-zinc-200 bg-white p-4 sm:p-6">
                      <WeightManager config={currentConfig} onSave={handleSave} onReset={handleReset} saving={saving} />
                    </div>
                  )}
                </div>
              ) : !allWeights ? (
                <div className="flex flex-col items-center justify-center py-16 rounded-lg border border-zinc-200 bg-white">
                  <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
                  <p className="mt-4 text-zinc-600">Loading weight configurations...</p>
                </div>
              ) : (
                <div className="rounded-lg border border-zinc-200 bg-white p-4 sm:p-6">
                  <WeightMultiEdit
                    allWeights={allWeights}
                    onSave={handleSave}
                    onSaveMultiple={handleSaveMultiple}
                    saving={saving}
                  />
                </div>
              )}
            </div>
          )}

          {activeTab === 'boosting' && (
            <div className="rounded-lg border border-zinc-200 bg-white p-4 sm:p-6">
              <RecordBoosting onSave={handleRecordBoostingSave} onReset={handleRecordBoostingReset} saving={saving} />
            </div>
          )}

          {activeTab === 'vector' && (
            <div className="rounded-lg border border-zinc-200 bg-white p-4 sm:p-6">
              <VectorSearchConfig onSave={handleVectorSearchSave} onReset={handleVectorSearchReset} saving={saving} />
            </div>
          )}

          {activeTab === 'fuzziness' && (
            <div className="rounded-lg border border-zinc-200 bg-white p-4 sm:p-6">
              <FuzzinessSettings />
            </div>
          )}

          {activeTab === 'sponsored' && currentPage === 'list' && (
            <div className="rounded-lg border border-zinc-200 bg-white p-4 sm:p-6">
              <SponsoredResults onAddResult={handleAddPromotedResult} onEditResult={handleEditPromotedResult} />
            </div>
          )}

          {activeTab === 'sponsored' && currentPage === 'form' && (
            <div className="rounded-lg border border-zinc-200 bg-white p-4 sm:p-6">
              <PromotedResultForm editingResult={editingResult} onSave={handleFormSave} onCancel={handleFormCancel} />
            </div>
          )}

          {activeTab === 'advertisements' && currentPage === 'list' && (
            <div className="rounded-lg border border-zinc-200 bg-white p-4 sm:p-6">
              <AdvertisementList onAddAd={handleAddAd} onEditAd={handleEditAd} />
            </div>
          )}

          {activeTab === 'advertisements' && currentPage === 'form' && (
            <div className="rounded-lg border border-zinc-200 bg-white p-4 sm:p-6">
              <AdvertisementForm editingAd={editingAd} onSave={handleAdFormSave} onCancel={handleAdFormCancel} />
            </div>
          )}

          {activeTab === 'modes' && (
            <div className="rounded-lg border border-zinc-200 bg-white p-4 sm:p-6">
              <SearchModeIndexSelector onSave={handleSearchModesSave} saving={saving} />
            </div>
          )}

          {activeTab === 'preview' && (
            <div className="rounded-lg border border-zinc-200 bg-white p-4 sm:p-6">
              <iframe
                src={import.meta.env.VITE_OPENSEARCH_DASHBOARD_URL || 'https://opensearch.asminternational.org'}
                className="w-full h-[calc(100vh-12rem)] border-0"
                title="OpenSearch Dashboard"
              />
            </div>
          )}

          {activeTab === 'statistics' && (
            <div className="rounded-lg border border-zinc-200 bg-white p-4 sm:p-6">
              <IndexStatistics apiUrl={import.meta.env.VITE_API_URL || ''} />
            </div>
          )}

          {activeTab === 'uat' && (
            <div className="rounded-lg border border-zinc-200 bg-white p-4 sm:p-6">
              <UATSessions />
            </div>
          )}

          {activeTab === 'feedback' && (
            <div className="rounded-lg border border-zinc-200 bg-white p-4 sm:p-6">
              <UserFeedbackList />
            </div>
          )}

          {activeTab === 'guide' && (
            <div className="rounded-lg border border-zinc-200 bg-white p-4 sm:p-6">
              <UserGuide />
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

export default App;
